/**
 * Detalle de un grupo ad-hoc (F4): responsable, credenciales que alcanza,
 * miembros con rol (editable por manager/responsable/admin), alta y baja de
 * miembros, borrado (solo sin credenciales).
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Info, Plus, Trash2, UserMinus } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { LoaderBlock } from '@/components/internal/shared';
import { AreaBadge, useVaultLabels } from './vault-ui';
import { errMsg } from '@/hooks/useVault';
import { VAULT_GROUP_ROLES, type VaultGroupRole } from '../../../../shared/types/vault';

export function GroupDetailCard({ groupId, onDeleted }: { groupId: string; onDeleted: () => void }) {
  const { t } = useTranslation();
  const L = useVaultLabels();
  const utils = trpc.useUtils();
  const detail = trpc.vault.getGroup.useQuery({ groupId }, { staleTime: 30_000 });
  const invalidate = () => {
    utils.vault.getGroup.invalidate({ groupId });
    utils.vault.listGroups.invalidate();
    utils.vault.getMyAccess.invalidate();
  };
  const onError = (e: { message?: string }) => toast.error(errMsg(e));
  const add = trpc.vault.addGroupMember.useMutation({ onSuccess: () => { invalidate(); setEmail(''); toast.success(t('vault.groups.member_added', 'Miembro agregado')); }, onError });
  const changeRole = trpc.vault.setGroupMemberRole.useMutation({ onSuccess: invalidate, onError });
  const remove = trpc.vault.removeGroupMember.useMutation({
    onSuccess: (r) => {
      invalidate();
      toast.success(t('vault.groups.member_removed', 'Miembro quitado · {{n}} credenciales marcadas para rotación', { n: r.flagged }));
      // el backend saca al miembro igual aunque alguna marca falle: que no pase en silencio
      if (r.failed > 0) toast.warning(t('vault.groups.remove_failed', '{{n}} credenciales no se pudieron marcar para rotación: revisalas a mano', { n: r.failed }));
    },
    onError,
  });
  const del = trpc.vault.deleteGroup.useMutation({ onSuccess: () => { invalidate(); toast.success(t('vault.groups.deleted', 'Grupo eliminado')); onDeleted(); }, onError });

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<VaultGroupRole>('reader');
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (detail.isLoading) return <Card className="p-4"><LoaderBlock /></Card>;
  if (!detail.data) return null;
  const { group, members, secrets, canManage } = detail.data;
  const canAdd = /^[^@\s]+@winclap\.com$/i.test(email.trim()) && !add.isPending;

  return (
    <Card className="divide-y">
      <div className="flex items-start justify-between gap-3 p-4">
        <div>
          <h2 className="font-semibold">{t('vault.groups.group_title', 'Grupo: {{name}}', { name: group.name })}</h2>
          {group.description && <p className="text-sm text-muted-foreground">{group.description}</p>}
        </div>
        <Badge variant="outline">{t('vault.groups.adhoc', 'Ad-hoc')}</Badge>
      </div>

      <dl className="grid grid-cols-[1fr_auto] gap-y-2 p-4 text-sm">
        <dt className="text-muted-foreground">{t('vault.groups.type', 'Tipo')}</dt>
        <dd className="text-right">{t('vault.groups.type_manual', 'Manual (proyecto / cliente)')}</dd>
        <dt className="text-muted-foreground">{t('vault.groups.responsible', 'Responsable del grupo')}</dt>
        <dd className="text-right">{group.responsibleEmail}</dd>
        <dt className="text-muted-foreground">{t('vault.groups.secrets_shared', 'Credenciales compartidas')}</dt>
        <dd className="text-right">{secrets.length}</dd>
      </dl>

      {secrets.length > 0 && (
        <ul className="divide-y px-4">
          {secrets.map((s) => (
            <li key={s.secretId} className="flex items-center gap-2 py-2 text-sm">
              <Link to={`/vault/secrets/${s.secretId}`} className="min-w-0 flex-1 truncate font-medium hover:underline">{s.name}</Link>
              <AreaBadge area={s.area} />
              <span className="text-xs text-muted-foreground">{s.permission ? L.perm(s.permission) : ''}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t('vault.groups.members', 'Miembros ({{n}})', { n: members.length })}</h3>
        </div>
        <ul className="divide-y">
          {members.map((m) => (
            <li key={m.email} className="flex items-center gap-2 py-2 text-sm">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold uppercase">{m.email.slice(0, 2)}</span>
              <span className="min-w-0 flex-1 truncate">{m.email}</span>
              {canManage ? (
                <>
                  <Select value={m.role} onValueChange={(v) => changeRole.mutate({ groupId, email: m.email, role: v as VaultGroupRole })}>
                    <SelectTrigger className="h-8 w-[120px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VAULT_GROUP_ROLES.map((r) => (
                        <SelectItem key={r} value={r}>{L.role(r)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="ghost" size="icon" title={t('vault.groups.remove', 'Quitar')} aria-label={t('vault.groups.remove', 'Quitar')} onClick={() => setConfirmRemove(m.email)}>
                    <UserMinus className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <Badge variant={m.role === 'manager' ? 'brand' : 'secondary'}>{L.role(m.role)}</Badge>
              )}
            </li>
          ))}
        </ul>

        {canManage && (
          <div className="grid gap-2 sm:grid-cols-[1fr_120px_auto]">
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nombre.apellido@winclap.com" />
            <Select value={role} onValueChange={(v) => setRole(v as VaultGroupRole)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {VAULT_GROUP_ROLES.map((r) => (
                  <SelectItem key={r} value={r}>{L.role(r)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" disabled={!canAdd} onClick={() => add.mutate({ groupId, email: email.trim(), role })}>
              <Plus className="mr-1 h-4 w-4" />
              {t('vault.groups.add', 'Agregar')}
            </Button>
          </div>
        )}

        <p className="flex items-start gap-2 rounded-md bg-sky-50 p-3 text-xs text-sky-900 dark:bg-sky-900/20 dark:text-sky-100">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t('vault.groups.remove_note', 'Al sacar a alguien del grupo, las credenciales que veía quedan con rotación sugerida y se avisa a los owners.')}
        </p>

        {canManage && (
          <div className="flex justify-end">
            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setConfirmDelete(true)} disabled={secrets.length > 0} title={secrets.length > 0 ? t('vault.groups.delete_blocked', 'Revocá primero sus credenciales') : undefined}>
              <Trash2 className="mr-1 h-4 w-4" />
              {t('vault.groups.delete', 'Eliminar grupo')}
            </Button>
          </div>
        )}
      </div>

      <AlertDialog open={!!confirmRemove} onOpenChange={(o) => !o && setConfirmRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('vault.groups.remove_title', '¿Sacar a {{email}} del grupo?', { email: confirmRemove ?? '' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('vault.groups.remove_body', 'Las credenciales que este grupo alcanza van a quedar marcadas para rotación y se avisa a sus owners.')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.ui.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction disabled={remove.isPending} onClick={() => { if (confirmRemove) remove.mutate({ groupId, email: confirmRemove }); setConfirmRemove(null); }}>
              {t('vault.groups.remove', 'Quitar')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('vault.groups.delete_title', '¿Eliminar el grupo "{{name}}"?', { name: group.name })}</AlertDialogTitle>
            <AlertDialogDescription>{t('vault.groups.delete_body', 'Se quitan sus miembros. Solo se puede eliminar un grupo sin credenciales compartidas.')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.ui.cancel', 'Cancelar')}</AlertDialogCancel>
            <AlertDialogAction onClick={() => del.mutate({ groupId })} disabled={del.isPending}>{t('vault.groups.delete', 'Eliminar grupo')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
